import React, { useEffect, useState, useRef, useCallback } from 'react';
import { GameEngine } from './game/state/gameEngine';
import { Camera } from './rendering/camera';
import { dbService } from './storage/database';
import { gameRepository } from './storage/gameRepository';
import { settingsRepository } from './storage/settingsRepository';
import { BoardCanvas } from './components/BoardCanvas';
import { Header } from './components/Header';
import { ControlOverlay } from './components/ControlOverlay';
import { UnlockDialog } from './components/UnlockDialog';
import { SettingsModal } from './components/SettingsModal';
import { RewardInfoModal } from './components/RewardInfoModal';
import { ThemeProvider, useTheme } from './theme/ThemeContext';
import { soundService } from './audio/soundEffects';
import { hapticsService } from './audio/haptics';

const MainContent: React.FC = () => {
  const { theme } = useTheme();

  const [engine, setEngine] = useState<GameEngine | null>(null);
  const [camera, setCamera] = useState<Camera | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  // App settings state
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [hapticsEnabled, setHapticsEnabled] = useState(true);

  // UI state
  const [flagMode, setFlagMode] = useState(false);
  const [selectedBustedBox, setSelectedBustedBox] = useState<{ boxX: number; boxY: number } | null>(
    null
  );
  const [showSettings, setShowSettings] = useState(false);
  const [showRewardsInfo, setShowRewardsInfo] = useState(false);

  // Toast warning state (3s timeout)
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const toastTimerRef = useRef<NodeJS.Timeout | null>(null);

  const showToast = useCallback((msg: string) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToastMessage(msg);
    toastTimerRef.current = setTimeout(() => {
      setToastMessage(null);
    }, 3000);
  }, []);

  // Render trigger counter
  const [, setRenderTick] = useState(0);
  const forceUpdate = useCallback(() => setRenderTick((tick) => tick + 1), []);

  // Save debounce timer
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Load Database & Initial Game State
  useEffect(() => {
    async function initApp() {
      try {
        await dbService.initialize();

        const loadedEngine = await gameRepository.loadGame();
        const loadedSettings = await settingsRepository.getSettings();

        setEngine(loadedEngine);
        setCamera(new Camera(loadedEngine.cameraX, loadedEngine.cameraY, loadedEngine.zoom));

        setSoundEnabled(loadedSettings.soundEnabled);
        soundService.enabled = loadedSettings.soundEnabled;

        setHapticsEnabled(loadedSettings.hapticsEnabled);
        hapticsService.enabled = loadedSettings.hapticsEnabled;

        setIsLoaded(true);
      } catch (err) {
        console.error('Failed to initialize app storage:', err);
      }
    }

    initApp();
  }, []);

  // Debounced Game State Auto-Saver
  const scheduleSaveGameState = useCallback(() => {
    if (!engine) return;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);

    saveTimeoutRef.current = setTimeout(() => {
      gameRepository.saveGameState(engine);
    }, 500);
  }, [engine]);

  // Camera change listener
  const handleCameraChange = useCallback(() => {
    if (!engine || !camera) return;
    engine.cameraX = camera.x;
    engine.cameraY = camera.y;
    engine.zoom = camera.zoom;
    scheduleSaveGameState();
    forceUpdate();
  }, [engine, camera, scheduleSaveGameState, forceUpdate]);

  // Handle cell reveal
  const handleRevealCell = useCallback(
    async (x: number, y: number) => {
      if (!engine) return;

      const result = engine.revealCell(x, y);

      if (result.rejectedUnexplored) {
        showToast('⚠️ Only tiles adjacent to revealed numbers can be explored!');
        hapticsService.vibrateLight();
        return;
      }

      if (result.hitMine && result.bustedBox) {
        soundService.playExplosion();
        hapticsService.vibrateHeavy();
        await gameRepository.saveBoxStatus(
          result.bustedBox.boxX,
          result.bustedBox.boxY,
          'BUSTED'
        );
      } else if (result.revealedCells.length > 0) {
        soundService.playReveal();
        hapticsService.vibrateLight();

        await gameRepository.saveCellsBatch(
          result.revealedCells.map((c) => ({ x: c.x, y: c.y, state: 'REVEALED' as const }))
        );

        if (result.completedBoxes.length > 0) {
          soundService.playCompletion();
          hapticsService.vibrateMedium();

          for (const box of result.completedBoxes) {
            await gameRepository.saveBoxStatus(box.boxX, box.boxY, 'COMPLETED');
          }
        }
      }

      await gameRepository.saveGameState(engine);
      forceUpdate();
    },
    [engine, forceUpdate, showToast]
  );

  // Handle cell flag toggle
  const handleToggleFlag = useCallback(
    async (x: number, y: number) => {
      if (!engine) return;

      const result = engine.toggleFlag(x, y);

      if (result.rejectedUnexplored) {
        showToast('⚠️ Only tiles adjacent to revealed numbers can be explored!');
        hapticsService.vibrateLight();
        return;
      }

      if (result.toggled && result.newState) {
        soundService.playFlag();
        hapticsService.vibrateLight();

        await gameRepository.saveCellState(x, y, result.newState);

        if (result.completedBoxes.length > 0) {
          soundService.playCompletion();
          hapticsService.vibrateMedium();

          for (const box of result.completedBoxes) {
            await gameRepository.saveBoxStatus(box.boxX, box.boxY, 'COMPLETED');
          }
        }
      }

      await gameRepository.saveGameState(engine);
      forceUpdate();
    },
    [engine, forceUpdate, showToast]
  );

  // Handle unlocking busted box
  const handleConfirmUnlock = useCallback(
    async (boxX: number, boxY: number) => {
      if (!engine) return;

      const res = engine.unlockBustedBox(boxX, boxY);
      if (res.success) {
        soundService.playUnlock();
        hapticsService.vibrateMedium();

        await gameRepository.saveBoxStatus(boxX, boxY, 'ACTIVE');
        await gameRepository.clearBoxCells(boxX, boxY);
        await gameRepository.saveGameState(engine);

        setSelectedBustedBox(null);
        forceUpdate();
      }
    },
    [engine, forceUpdate]
  );

  // Handle Reset Game
  const handleResetGame = useCallback(async () => {
    if (!engine || !camera) return;

    const newSeed = Math.random().toString(36).substring(2, 10);
    engine.resetGame(newSeed);
    camera.x = 0;
    camera.y = 0;
    camera.zoom = 1.0;

    await gameRepository.resetGameData(newSeed);
    forceUpdate();
  }, [engine, camera, forceUpdate]);

  // Handle Audio & Haptics Toggles
  const handleToggleSound = (enabled: boolean) => {
    setSoundEnabled(enabled);
    soundService.enabled = enabled;
    settingsRepository.setSetting('soundEnabled', enabled ? 'true' : 'false');
  };

  const handleToggleHaptics = (enabled: boolean) => {
    setHapticsEnabled(enabled);
    hapticsService.enabled = enabled;
    settingsRepository.setSetting('hapticsEnabled', enabled ? 'true' : 'false');
  };

  if (!isLoaded || !engine || !camera) {
    return (
      <div
        style={{
          width: '100vw',
          height: '100vh',
          backgroundColor: '#000000',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#ffffff',
          fontFamily: '"Outfit", sans-serif',
          fontWeight: 700,
        }}
      >
        Initializing Expanding Minefield...
      </div>
    );
  }

  return (
    <div
      style={{
        width: '100vw',
        height: '100vh',
        backgroundColor: theme.bg,
        overflow: 'hidden',
        position: 'relative',
        userSelect: 'none',
      }}
    >
      <Header
        rewardPoints={engine.rewardPoints}
        onOpenRewardsInfo={() => setShowRewardsInfo(true)}
        onOpenSettings={() => setShowSettings(true)}
      />

      {/* Floating 3s Warning Toast */}
      {toastMessage && (
        <div
          style={{
            position: 'absolute',
            top: '72px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'rgba(239, 68, 68, 0.92)',
            color: '#ffffff',
            padding: '10px 20px',
            borderRadius: '9999px',
            fontFamily: '"Outfit", sans-serif',
            fontWeight: 700,
            fontSize: '14px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
            zIndex: 100,
            backdropFilter: 'blur(8px)',
            pointerEvents: 'none',
            textAlign: 'center',
            maxWidth: '90vw',
            lineHeight: 1.3,
          }}
        >
          {toastMessage}
        </div>
      )}

      <BoardCanvas
        engine={engine}
        camera={camera}
        theme={theme}
        flagMode={flagMode}
        onRevealCell={handleRevealCell}
        onToggleFlag={handleToggleFlag}
        onSelectBustedBox={(boxX, boxY) => setSelectedBustedBox({ boxX, boxY })}
        onCameraChange={handleCameraChange}
      />

      <ControlOverlay
        flagMode={flagMode}
        onToggleFlagMode={() => setFlagMode(!flagMode)}
        onRecenter={() => {
          camera.x = 0;
          camera.y = 0;
          handleCameraChange();
        }}
        onZoomIn={() => {
          camera.setZoom(camera.zoom * 1.2);
          handleCameraChange();
        }}
        onZoomOut={() => {
          camera.setZoom(camera.zoom * 0.8);
          handleCameraChange();
        }}
      />

      {selectedBustedBox && (
        <UnlockDialog
          boxX={selectedBustedBox.boxX}
          boxY={selectedBustedBox.boxY}
          failureCount={engine.failureCount}
          rewardPoints={engine.rewardPoints}
          onConfirmUnlock={handleConfirmUnlock}
          onClose={() => setSelectedBustedBox(null)}
        />
      )}

      {showSettings && (
        <SettingsModal
          soundEnabled={soundEnabled}
          hapticsEnabled={hapticsEnabled}
          onToggleSound={handleToggleSound}
          onToggleHaptics={handleToggleHaptics}
          onResetGame={handleResetGame}
          onClose={() => setShowSettings(false)}
        />
      )}

      {showRewardsInfo && (
        <RewardInfoModal
          rewardPoints={engine.rewardPoints}
          failureCount={engine.failureCount}
          onClose={() => setShowRewardsInfo(false)}
        />
      )}
    </div>
  );
};

export default function App() {
  return (
    <ThemeProvider>
      <MainContent />
    </ThemeProvider>
  );
}
